/**
 * Generic message queue implementation for queueing messages until a listener is ready to process them
 */
export function createMessageQueue<T>() {
	let queue: T[] = [];
	let isReady = false;

	return {
		/**
		 * Add a message to the queue
		 */
		enqueue(message: T): void {
			queue.push(message);
		},

		/**
		 * Check if there are messages in the queue
		 */
		hasMessages(): boolean {
			return queue.length > 0;
		},

		/**
		 * Get the number of messages in the queue
		 */
		size(): number {
			return queue.length;
		},

		/**
		 * Mark the queue as ready to process messages
		 */
		setReady(): void {
			isReady = true;
		},

		/**
		 * Check if the queue is ready to process messages
		 */
		isListenerReady(): boolean {
			return isReady;
		},

		/**
		 * Process all queued messages asynchronously to avoid blocking UI
		 * Uses setTimeout to defer processing to the next event loop cycle
		 * @param processor Function to process each message
		 */
		processAndClearAsync(processor: (message: T) => void): void {
			if (queue.length > 0) {
				const messages = [...queue];
				queue = [];

				setTimeout(() => {
					messages.forEach(processor);
				}, 0);
			}
		},

		/**
		 * Clear all messages from the queue without processing
		 */
		clear(): void {
			queue = [];
		},
	};
}
